<template>
    <div v-if="$auth.check('ROLE_ADMIN')">

        <v-row align="center">
            <v-col cols="12" sm="7">
                <h1>Voxxer v1.0</h1>
            </v-col>
            <v-col cols="12" sm="5" class="text-sm-right">
                <span class="text-caption grey--text mr-3" v-if="updated_at">Atualizado às {{ updated_at }}</span>
                <v-btn color="primary" small :loading="pending>0" @click="loadAll()">
                    <v-icon left small>mdi-refresh</v-icon>Atualizar
                </v-btn>
            </v-col>
        </v-row>

        <v-progress-linear indeterminate color="primary" class="mb-3" v-if="pending>0"></v-progress-linear>

        <v-alert dense text type="warning" v-if="failures.length">
            Falha ao carregar: {{ failures.join(", ") }}
        </v-alert>

        <v-row>
            <v-col cols="12" sm="6" md="4" v-for="item in kpis" :key="item.key">
                <v-card :to="item.to" outlined>
                    <v-card-text class="pb-0">
                        <v-icon :color="item.color" small class="mr-1">{{ item.icon }}</v-icon>
                        <span class="text-subtitle-2">{{ item.title }}</span>
                    </v-card-text>
                    <v-card-text class="py-1 text-h4 text--primary">
                        <span v-if="item.value === null" class="grey--text">—</span>
                        <span v-else>{{ item.value }}</span>
                    </v-card-text>
                    <v-card-text class="pt-0 text-caption grey--text">{{ item.sub }}</v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Linhas SIP por status</v-card-title>
                    <v-card-text>
                        <doughnut-chart v-if="data_sipline_status" :chart-data="data_sipline_status" :options="doughnut_options" :styles="doughnut_styles"/>
                        <div v-else class="text-caption grey--text">Sem dados</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Números DID por status</v-card-title>
                    <v-card-text>
                        <doughnut-chart v-if="data_did_status" :chart-data="data_did_status" :options="doughnut_options" :styles="doughnut_styles"/>
                        <div v-else class="text-caption grey--text">Sem dados</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="7">
                <v-card>
                    <v-card-title>Clientes por plano</v-card-title>
                    <v-card-text>
                        <bar-chart v-if="data_plan" :chart-data="data_plan" :options="bar_options" :styles="bar_styles"/>
                        <div v-else class="text-caption grey--text">Sem dados</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="5">
                <v-card>
                    <v-card-title>Top 10 cidades</v-card-title>
                    <v-card-text>
                        <bar-chart v-if="data_city" :chart-data="data_city" :options="bar_options" :styles="bar_styles"/>
                        <div v-else class="text-caption grey--text">Sem dados</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="8">
                <v-card>
                    <v-card-title>
                        Chamadas em curso
                        <v-spacer></v-spacer>
                        <v-chip small color="primary" v-if="channels">{{ channels.length }}</v-chip>
                    </v-card-title>
                    <v-card-text v-if="channels && channels.length==0" class="text-caption grey--text">
                        Nenhuma chamada em curso.
                    </v-card-text>
                    <v-simple-table v-if="channels && channels.length>0">
                        <thead>
                            <tr>
                                <th>Origem</th>
                                <th>Destino</th>
                                <th>Servidor</th>
                                <th>Início</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in channels_top" :key="item.id">
                                <td>{{ item.source }}</td>
                                <td>{{ item.number }}</td>
                                <td>{{ item.server }}</td>
                                <td>{{ item.time | formatDate }} {{ item.time | formatTime }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <v-card-actions v-if="channels && channels.length>channels_limit">
                        <v-btn text small color="primary" to="/channel">Ver todas</v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title>Atalhos</v-card-title>
                    <v-list dense>
                        <v-list-item v-for="item in shortcuts" :key="item.to" link :to="item.to">
                            <v-list-item-action>
                                <v-icon>{{ item.icon }}</v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>
        </v-row>

    </div>
</template>

<script>
import api from '../api/index.js';
import BarChart from '../components/BarChart.js';
import DoughnutChart from '../components/DoughnutChart.js';

const STATUS_LABELS = ["Habilitado", "Bloq. administrador", "Bloq. cliente", "Bloq. pagamento", "Cancelado"];
const STATUS_COLORS = ["#4CAF50", "#FB8C00", "#2196F3", "#FF5252", "#9E9E9E"];
const CHANNEL_REFRESH = 20000;

export default {
  components: { BarChart, DoughnutChart },
  data: () => ({
    client_count: null,
    sipline_status: null,
    did_status: null,
    plan: null,
    city: null,
    trunks: null,
    channels: null,
    channels_limit: 8,
    pending: 0,
    failures: [],
    updated_at: null,
    timer: null,
    shortcuts: [
      { to: "/sipline/report", icon: "mdi-chart-bar", text: "Relatório de linhas SIP" },
      { to: "/did/report", icon: "mdi-chart-bar", text: "Relatório de números DID" },
      { to: "/credit", icon: "mdi-cash-plus", text: "Créditos" },
      { to: "/client/compels", icon: "mdi-connection", text: "Integração Compels" },
      { to: "/channel", icon: "mdi-timer", text: "Tempo real" },
      { to: "/log", icon: "mdi-file-chart", text: "Logs" }
    ],
    bar_options: {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
      }
    },
    doughnut_options: {
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: "bottom" }
    },
    bar_styles: {
      position: "relative",
      height: "300px",
      width: "100%"
    },
    doughnut_styles: {
      position: "relative",
      height: "280px",
      width: "100%"
    }
  }),
  created: function() {
    this.loadAll();
    this.timer = setInterval(this.loadChannels, CHANNEL_REFRESH);
  },
  beforeDestroy: function() {
    clearInterval(this.timer);
  },
  computed: {
    channels_top: function() {
      if (!this.channels) {
        return [];
      }
      return this.channels.slice(0, this.channels_limit);
    },
    data_sipline_status: function() {
      return this.statusChart(this.sipline_status);
    },
    data_did_status: function() {
      return this.statusChart(this.did_status);
    },
    data_plan: function() {
      return this.rankChart(this.plan, "#FB8C00", 12);
    },
    data_city: function() {
      return this.rankChart(this.city, "#1976D2", 10);
    },
    kpis: function() {
      return [
        {
          key: "client",
          title: "Clientes",
          value: this.client_count,
          sub: "cadastrados",
          icon: "mdi-account-box-outline",
          color: "primary",
          to: "/client"
        },
        {
          key: "sipline",
          title: "Linhas SIP",
          value: this.total(this.sipline_status),
          sub: this.enabledLabel(this.sipline_status, "habilitadas"),
          icon: "mdi-phone-outgoing",
          color: "primary",
          to: "/sipline"
        },
        {
          key: "did",
          title: "Números DID",
          value: this.total(this.did_status),
          sub: this.enabledLabel(this.did_status, "habilitados"),
          icon: "mdi-phone-incoming",
          color: "primary",
          to: "/did"
        },
        {
          key: "channel",
          title: "Chamadas em curso",
          value: this.channels ? this.channels.length : null,
          sub: "atualiza a cada 20s",
          icon: "mdi-timer",
          color: "accent",
          to: "/channel"
        },
        {
          key: "trunk",
          title: "Troncos",
          value: this.trunks ? this.trunks.length : null,
          sub: this.trunks ? this.trunks.filter(function(item) { return item.enabled; }).length + " habilitados" : "",
          icon: "mdi-arrow-decision-outline",
          color: "primary",
          to: "/trunk"
        }
      ];
    }
  },
  methods: {
    fetch: function(path, label, apply) {
      var me = this;
      var token = localStorage.getItem("token");
      me.pending++;
      api.get(path, {headers: { Authorization: "Bearer " + token }})
      .then(function(response) {
        me.pending--;
        me.updated_at = new Date().toLocaleTimeString("pt-BR");
        apply(response.data);
      })
      .catch(function(error) {
        me.pending--;
        console.log(error);
        if(me.failures.indexOf(label)<0){
          me.failures.push(label);
        }
      });
    },
    loadAll: function() {
      var me = this;
      me.failures = [];
      me.fetch("/client/count", "clientes", function(data) { me.client_count = data; });
      me.fetch("/sipline/report/status", "linhas SIP", function(data) { me.sipline_status = data; });
      me.fetch("/did/report/status", "números DID", function(data) { me.did_status = data; });
      me.fetch("/sipline/report/plan", "clientes por plano", function(data) { me.plan = data; });
      me.fetch("/sipline/report/city", "clientes por cidade", function(data) { me.city = data; });
      me.fetch("/trunk", "troncos", function(data) { me.trunks = data; });
      me.loadChannels();
    },
    loadChannels: function() {
      var me = this;
      me.fetch("/channel", "chamadas em curso", function(data) { me.channels = data; });
    },
    total: function(rows) {
      if (!rows) {
        return null;
      }
      return rows.reduce(function(sum, row) { return sum + row[1]; }, 0);
    },
    enabledLabel: function(rows, word) {
      if (!rows) {
        return "";
      }
      var enabled = rows.filter(function(row) { return row[0]==0; });
      return (enabled.length ? enabled[0][1] : 0) + " " + word;
    },
    statusChart: function(rows) {
      if (!rows || rows.length==0) {
        return null;
      }
      return {
        labels: rows.map(function(row) { return STATUS_LABELS[row[0]] || "Desconhecido"; }),
        datasets: [{
          backgroundColor: rows.map(function(row) { return STATUS_COLORS[row[0]] || "#607D8B"; }),
          data: rows.map(function(row) { return row[1]; })
        }]
      };
    },
    rankChart: function(rows, color, limit) {
      if (!rows || rows.length==0) {
        return null;
      }
      var top = rows.slice().sort(function(a, b) { return b[1] - a[1]; }).slice(0, limit);
      return {
        labels: top.map(function(row) { return row[0]; }),
        datasets: [{
          label: "Clientes",
          backgroundColor: color,
          data: top.map(function(row) { return row[1]; })
        }]
      };
    }
  }
};
</script>
